"use client";

import React, { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function HeatPumpInstallationDublin() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="224107" id="heat-pump-installation-dublin" className="dark relative w-full h-[100dvh] min-h-[600px] flex items-end bg-background overflow-hidden">
      {}
      <div className="absolute inset-0 z-0">
        <video autoPlay loop muted playsInline className="w-full h-full object-cover" poster="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689081/img/modern_home_heat_pump_exterior.png">
          {}
          <source src="https://cdn.pixabay.com/video/2020/05/25/40131-424917300_large.mp4" type="video/mp4" />
        </video>
        
        {}
        <div className="absolute inset-0 bg-gradient-to-tr from-background/95 via-background/60 to-primary/20 pointer-events-none" />
        <div className="absolute inset-0 bg-black/20 pointer-events-none" /> {}
      </div>

      {}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-16 md:pb-24 lg:pb-32">
        <div ref={ref} className={`max-w-3xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 border border-primary/20 mb-6">
            <span className="font-mono text-xs uppercase tracking-wider text-primary">SEAI Registered Installer</span>
          </div>

          <h1 className="font-default text-6xl md:text-8xl lg:text-[7rem] text-foreground font-bold leading-[1.05] tracking-tighter mb-6">
            The Future <br className="hidden md:block" />
            of Heating.
          </h1>
          
          <p className="font-default text-[1.2rem] text-muted-foreground max-w-xl leading-relaxed mb-10">
            Upgrade to sustainable comfort with D4 Plumbing & Heating. Professional heat pump installations in Dublin designed for maximum efficiency and lower energy bills.
          </p>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Link to="/HeatPumpsPage">
              <Button size="lg" className="h-14 px-8 text-base w-full sm:w-auto font-default font-semibold shadow-lg shadow-primary/20">
                Get a Free Quote
              </Button>
            </Link>
            
            <Button variant="outline" size="lg" className="h-14 px-8 text-base w-full sm:w-auto border-border text-foreground hover:bg-accent/10 backdrop-blur-sm gap-3 font-default">
              <Play className="size-5 fill-primary text-primary" />
              How It Works
            </Button>
          </div>
        </div>
      </div>
    </section>;
}